import React from "react";
import styled from "styled-components";
import Router, { useRouter } from "next/router";
import { bool } from "prop-types";
import getConfig from "next/config";

import RedirectBrandIdForms from "components/RedirectBrandIdForms";
import ContentWrapper from "@components/shared/ContentWrapper";
import Layout from "@components/Layout/Layout";
import ErrorBoundary from "@components/shared/ErrorBoundary";

import { color } from "@components/shared/utils";
import { ROUTES } from "@shared/routing/routes";
import { useRoutingUtils } from "@hooks/useRoutingUtils";
import RoutingUtils from "@shared/routing/utils";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useEffect } from "react";
import { updateMyAccountLoginVirtualUrlEvent } from "@gtm/events/myaccount";
import { useReferrerSelector } from "@redux/reducers/appConfig";
import { useExternalConfigurationPathname } from "@hooks/useExternalConfigurationPathname";
import { BRANDID_REGISTRATION_TYPES, IS_SERVER } from "@shared/constants";
import { useGetBrandIdRedirect } from "@hooks/useGetBrandIdRedirect";
import { usePrimaryColor } from "@hooks/usePrimaryColor";
import { i18n, useTranslation, withTranslation } from "@lib/i18n";

import gtm from "@gtm/core";

const { publicRuntimeConfig } = getConfig();
const { ROOT_URL_PREFIX } = publicRuntimeConfig;

const MainContent = styled.div`
  width: 100%;
  background: ${color("white")};
  color: ${color("black")};
  margin: 4rem 0;
`;

const LoginPage = ({ isSSRRendered }) => {
  const { i18n } = useTranslation();
  const primaryColor = usePrimaryColor();
  const router = useRouter();
  const routingUtils = useRoutingUtils();
  const isUserTrusted = useIsUserTrustedSelector();
  const isB2BSelected = useIsB2BSelected();
  const affiliation = useReferrerSelector();
  const externalConfigurationPathname = useExternalConfigurationPathname();
  const getBrandIdRegistrationRedirect = useGetBrandIdRedirect();

  useEffect(() => {
    if (isUserTrusted && router.query.callbackUrl) {
      router.replace(
        ROUTES.MY_ACCOUNT_INDEX,
        routingUtils.getLocalizedPathname(
          ROUTES.MY_ACCOUNT_INDEX,
          null,
          i18n.language
        )
      );
    }

    gtm.fire(
      updateMyAccountLoginVirtualUrlEvent({
        pageTitle: router.asPath,
        affiliation,
        isB2BSelected,
        isSSRRendered,
      })
    );
  }, []);

  const getLoginRedirect = () => {
    const { callbackUrl } = router.query;

    if (!callbackUrl) {
      return externalConfigurationPathname;
    }

    return routingUtils.buildPathname(callbackUrl, {
      token: router.query.token,
    });
  };

  return (
    <Layout>
      <ContentWrapper isWholeWidth color={primaryColor} dataId="basket">
        <MainContent>
          <ErrorBoundary>
            <RedirectBrandIdForms
              disabled
              defaultOpen
              redirectLogin={getLoginRedirect()}
              redirectRegistration={getBrandIdRegistrationRedirect(
                ROUTES.MY_ACCOUNT_LOGIN
              )}
              registrationType={
                isB2BSelected ? BRANDID_REGISTRATION_TYPES.B2B_LOGIN_PAGE : null
              }
            />
          </ErrorBoundary>
        </MainContent>
      </ContentWrapper>
    </Layout>
  );
};

const serverGetInitialProps = async ctx => {
  const debug = require("debug")("app:ssr-login-page");

  const { query, req, res, store } = ctx;
  const { session } = req;
  const { appConfig } = store.getState();

  const {
    language,
    brandId: { isTrusted },
  } = session;

  const routingUtils = new RoutingUtils({
    routes: appConfig.routes,
  });

  if (isTrusted) {
    const myAccountRoute =
      ROOT_URL_PREFIX +
      routingUtils.buildPathname(
        routingUtils.getLocalizedPathname(
          ROUTES.MY_ACCOUNT_INDEX,
          null,
          language
        ),
        query
      );

    debug(
      `ℹ️  LoginPage was accessed while already logged in. Redirecting to: ${myAccountRoute}`
    );

    await res.redirect(myAccountRoute);

    return;
  }

  return { namespacesRequired: ["common"] };
};

const clientGetInitialProps = async ctx => {
  const { query, store } = ctx;
  const language = i18n.language;
  const { auth, appConfig } = store.getState();

  const routingUtils = new RoutingUtils({
    routes: appConfig.routes,
  });

  if (auth.isTrusted) {
    const myAccountRoute = routingUtils.buildPathname(
      routingUtils.getLocalizedPathname(
        ROUTES.MY_ACCOUNT_INDEX,
        null,
        language
      ),
      query
    );

    await Router.push(myAccountRoute);

    return;
  }

  return { namespacesRequired: ["common"] };
};

LoginPage.getInitialProps = async ctx =>
  IS_SERVER
    ? await serverGetInitialProps(ctx)
    : await clientGetInitialProps(ctx);

LoginPage.propTypes = {
  isSSRRendered: bool,
};

export default withTranslation("common")(LoginPage);
